
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface TeacherSubject {
  id: string;
  teacher_code: string;
  subject: string;
  grade_level: string;
  course_type: 'Regular' | 'Inter';
  created_at: string;
  updated_at: string;
}

export function useTeacherSubjects(teacherCode: string | null) {
  const { toast } = useToast();
  const [subjects, setSubjects] = useState<TeacherSubject[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSubjects = async () => {
    if (!teacherCode) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    
    const { data, error } = await supabase
      .from('teacher_subjects')
      .select('*')
      .eq('teacher_code', teacherCode)
      .order('subject', { ascending: true });

    if (error) {
      console.error('Error fetching teacher subjects:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถโหลดข้อมูลวิชาที่สอนได้',
        variant: 'destructive'
      });
    } else {
      setSubjects((data || []) as TeacherSubject[]);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    fetchSubjects();
  }, [teacherCode, toast]);

  const addSubject = async (subjectData: Omit<TeacherSubject, 'id' | 'created_at' | 'updated_at'>) => {
    const { data, error } = await supabase
      .from('teacher_subjects')
      .insert(subjectData)
      .select()
      .single();

    if (error) {
      console.error('Error adding subject:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถเพิ่มวิชาได้',
        variant: 'destructive'
      });
      return false;
    }

    setSubjects(prev => [...prev, data as TeacherSubject]);
    toast({
      title: 'เพิ่มสำเร็จ',
      description: 'เพิ่มวิชาที่สอนแล้ว'
    });
    return true;
  };

  const updateSubject = async (id: string, updates: Partial<TeacherSubject>) => {
    const { data, error } = await supabase
      .from('teacher_subjects')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating subject:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถอัพเดทวิชาได้',
        variant: 'destructive'
      });
      return false;
    }

    setSubjects(prev => prev.map(subject => 
      subject.id === id ? data as TeacherSubject : subject
    ));
    toast({
      title: 'อัพเดทสำเร็จ',
      description: 'อัพเดทข้อมูลวิชาแล้ว'
    });
    return true;
  };

  const deleteSubject = async (id: string) => {
    const { error } = await supabase
      .from('teacher_subjects')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting subject:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถลบวิชาได้',
        variant: 'destructive'
      });
      return false;
    }

    setSubjects(prev => prev.filter(subject => subject.id !== id));
    toast({
      title: 'ลบสำเร็จ',
      description: 'ลบวิชาที่สอนแล้ว'
    });
    return true;
  };

  const addMultipleSubjects = async (
    subjectsData: Omit<TeacherSubject, 'id' | 'created_at' | 'updated_at'>[]
  ) => {
    const { data, error } = await supabase
      .from('teacher_subjects')
      .insert(subjectsData)
      .select();

    if (error) {
      console.error('Error adding multiple subjects:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถเพิ่มวิชาได้',
        variant: 'destructive'
      });
      return false;
    }

    setSubjects(prev => [...prev, ...(data as TeacherSubject[])]);
    return true;
  };

  return {
    subjects,
    isLoading,
    addSubject,
    updateSubject,
    deleteSubject,
    addMultipleSubjects,
    refetch: fetchSubjects
  };
}
